import { Column } from "@tanstack/react-table"
import { Button } from "common/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "common/ui/dropdown-menu"
import { ArrowDown, ArrowUp, ChevronsUpDown, EyeOff } from "lucide-react"
import { useSearchParams } from "react-router-dom"
import { cn } from "utils/cn"

interface DataTableColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>
  title: string
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const [searchParams, setSearchParams] = useSearchParams()
  const { id } = column || {}

  const currentOrdering = searchParams.get("ordering") || ""

  if (!column.getCanSort()) {
    return <div className={cn(className)}>{title}</div>
  }

  return (
    <div className={cn("flex items-center space-x-2 justify-end", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild >
          <Button
            variant="none"
            size="sm"
            className={cn("gap-1 px-0 text-sm text-muted-foreground", className)}
          >
            <span>{title}</span>
            {currentOrdering === id ? (
              <ArrowUp className="size-4" />
            ) : currentOrdering === `-${id}` ? (
              <ArrowDown className="size-4" />
            ) : (
              <ChevronsUpDown className="size-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            onClick={() =>
              setSearchParams({
                ...Object.fromEntries(searchParams),
                ordering: id,
              })
            }
          >
            <ArrowUp className="h-3.5 w-3.5 text-muted-foreground/70" />
            Asc
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              setSearchParams({
                ...Object.fromEntries(searchParams),
                ordering: `-${id}`,
              })
            }
          >
            <ArrowDown className="h-3.5 w-3.5 text-muted-foreground/70" />
            Desc
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
            <EyeOff className="h-3.5 w-3.5 text-muted-foreground/70" />
            Hide
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}